import {useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {Controller, useFormContext} from "react-hook-form";
import {useSchool} from "../school/SchoolContext";

import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

function ScheduleForm({namePrefix, schedules, selectedSchedulesIds, onScheduleIdchange}) {
    const {control} = useFormContext();

    const intl = useIntl();

    // Sans file d'attente, un horaire complet reste affiché mais ne peut plus être choisi.
    const waitingListEnabled = useSchool().options?.WAITING_LIST !== false;

    // Modale d'avertissement quand la famille choisit un horaire complet (file d'attente).
    const [fullDialogOpen, setFullDialogOpen] = useState(false);

    const name = (name) => {
        return namePrefix ? namePrefix + name : name;
    };

    const hasSchedules = () => {
        return schedules?.length;
    };

    const DAY_LABELS = {
        lundi: "Lundi", mardi: "Mardi", mercredi: "Mercredi", jeudi: "Jeudi",
        vendredi: "Vendredi", samedi: "Samedi", dimanche: "Dimanche"
    };

    // Jour non défini : valeur "a_confirmer" (admin) ou absente.
    const isDayUnknown = (schedule) => schedule.day == null || schedule.day === "a_confirmer";

    // Heures non définies : case « heure à définir » cochée (waiting) ou heures absentes.
    const areHoursUnknown = (schedule) => schedule.waiting === true || schedule.start == null || schedule.end == null;

    const getScheduleLabel = (schedule) => {
        const timeLabel = getScheduleTimeLabel(schedule);
        const name = schedule.label?.trim();
        return name ? name + " — " + timeLabel : timeLabel;
    };

    const getScheduleTimeLabel = (schedule) => {
        if (schedule.statusDto === "ON_DEMAND" || schedule.statusDto === "UNITE_COURSE") {
            return intl.formatMessage({id: "on_demand"});
        }
        // Jour inconnu (« à confirmer ») → « Horaires à venir »
        if (isDayUnknown(schedule) || schedule.statusDto === "TO_CONFIRM") {
            return intl.formatMessage({id: "to_confirm"});
        }
        const day = DAY_LABELS[schedule.day] ?? schedule.day;
        // Jour connu mais heures non définies → « Mardi (horaires à venir) »
        if (areHoursUnknown(schedule)) {
            return day + " " + intl.formatMessage({id: "to_confirm_hours"});
        }
        let label = day + " de " + schedule.start.substring(0, 5) + " à " + schedule.end.substring(0, 5);
        if (schedule.statusDto == "FULL"){
            label += " (COMPLET)";
        }
        return label;
    };

    const isScheduleAlreadySelected = (id) => {
        return !!(selectedSchedulesIds || []).includes(id);
    };

    const isScheduleNotValid = (schedule) => {
        // Jour ou heures non définis (« à confirmer » / « heure à définir ») → non sélectionnable.
        return isDayUnknown(schedule) ||
            areHoursUnknown(schedule) ||
            schedule.statusDto === "ON_DEMAND" ||
            schedule.statusDto === "TO_CONFIRM" ||
            schedule.statusDto === "UNITE_COURSE";
    }

    const isScheduleDisabled = (schedule) => {
        if (isScheduleNotValid(schedule)) return true;
        if (schedule.statusDto === "FULL" && !waitingListEnabled) return true;
        return !!(selectedSchedulesIds || []).includes(schedule.id);
    };

    const validateScheduleId = (value) => {
        const isValid = schedules.some((schedule) => schedule.id === value);
        return isValid || intl.formatMessage({id: "error_required_schedule"});
    };

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Controller
                        control={control}
                        name={name("")}
                        rules={{
                            required: true,
                            validate: {
                                validScheduleId: validateScheduleId
                            }
                        }}
                        render={({field, fieldState}) => (
                            <TextField
                                {...field}
                                disabled={!hasSchedules()}
                                required
                                select
                                fullWidth
                                variant="outlined"
                                size="small"
                                error={fieldState.invalid}
                                label={intl.formatMessage({id: "discipline_schedule"})}
                                onChange={(event) => {
                                    field.onChange(event);
                                    onScheduleIdchange();
                                    const selected = (schedules || []).find((schedule) => schedule.id === event.target.value);
                                    if (selected?.statusDto === "FULL" && waitingListEnabled) {
                                        setFullDialogOpen(true);
                                    }
                                }}
                            >
                                {(schedules || []).filter(schedule => !schedule.hide).map((schedule) => (
                                    <MenuItem
                                        key={schedule.id}
                                        value={schedule.id}
                                        disabled={isScheduleDisabled(schedule)}
                                    >
                                        {getScheduleLabel(schedule)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    />
                </Grid>
            </Grid>

            <Dialog open={fullDialogOpen} onClose={() => setFullDialogOpen(false)}>
                <DialogTitle>
                    <FormattedMessage id="schedule_full_title"/>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <FormattedMessage id="schedule_full_message"/>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button autoFocus onClick={() => setFullDialogOpen(false)}>
                        <FormattedMessage id="schedule_full_close"/>
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default ScheduleForm;
